// Qué botón y qué textos tocan en «Pausar» de la ficha del cliente (06-10).
//
// Hay dos pausas y la pantalla no sabe en qué entorno está: decide solo por lo que llega.
//   - La de siempre (core/pausa.py): inmediata y sin plazo. El detalle trae `profile.pausa`
//     mientras dura y nada más.
//   - La del catálogo: se PIDE, empieza al acabar la semana 4 o la 8 del plan, dura 4
//     semanas, el plan se alarga 4 y al terminar el cliente vuelve a la semana 1. El detalle
//     trae además `profile.pausa_estado` (ninguna | pedida | en_curso) y `profile.pausa_detalle`
//     ({empieza_el, desde, hasta, del_catalogo}, días AAAA-MM-DD ya resueltos en España).
// Si `pausa_estado` no viene, todo queda exactamente como antes.
//
// Lógica pura: sin React y sin red. La usa ClientDetailPage.

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto',
    'septiembre', 'octubre', 'noviembre', 'diciembre'];

/** Un día AAAA-MM-DD dicho «12 de octubre». Es un día ya resuelto: se lee del texto, sin
 *  pasar por `Date` ni por zonas horarias. Devuelve null si no es un día. */
export const diaEnTexto = (dia) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dia ?? ''));
    if (!m) return null;
    const mes = MESES[Number(m[2]) - 1];
    const d = Number(m[3]);
    if (!mes || d < 1 || d > 31) return null;
    return `${d} de ${mes}`;
};

const ESTADOS = ['ninguna', 'pedida', 'en_curso'];

const fraseDePedida = (empiezaEl) => {
    const dia = diaEnTexto(empiezaEl);
    return dia ? `Pausa pedida: empieza el ${dia} y dura 4 semanas`
        : 'Pausa pedida: empieza al acabar la semana 4 o la 8 de su plan';
};

const QUITAR_DE_SIEMPRE = {
    title: '¿Quitar la pausa?',
    description: 'Sus semanas vuelven a contar desde donde se quedaron, y la fecha de fin de lo que tiene pagado se alarga las semanas que ha estado en pausa.',
    confirmLabel: 'Quitar la pausa',
};

/**
 * Lo que toca pintar para este cliente. Devuelve:
 *   accion        'pausar' | 'retirar' | 'quitar'
 *   pausar        lo que se manda en el POST (`{pausar}`)
 *   boton         el texto del botón
 *   testid        el `data-testid` del botón
 *   marcado       si el botón va resaltado (hay una pausa pedida o en curso)
 *   aviso         la frase junto al botón, o null
 *   avisoTestid   el `data-testid` de esa frase
 *   confirmar     { title, description, confirmLabel } del diálogo
 *   errorFijo     la frase fija si el POST falla sin una frase humana
 *   delCatalogo   si el backend habla de la pausa del catálogo (trae `pausa_estado`)
 */
export const estadoDeLaPausa = (profile) => {
    const p = profile || {};
    const detalle = p.pausa_detalle || {};
    const delCatalogo = ESTADOS.includes(p.pausa_estado);

    if (delCatalogo && p.pausa_estado === 'pedida') {
        return {
            accion: 'retirar', pausar: false, boton: 'Retirar la pausa pedida', testid: 'retirar-pausa',
            marcado: true, aviso: fraseDePedida(detalle.empieza_el), avisoTestid: 'pausa-pedida',
            confirmar: {
                title: '¿Retirar la pausa pedida?',
                description: 'La pausa no llega a empezar y su plan sigue como estaba.',
                confirmLabel: 'Retirar la pausa',
            },
            errorFijo: 'No se pudo retirar la pausa', delCatalogo,
        };
    }

    const enCurso = delCatalogo ? (p.pausa_estado === 'en_curso' || !!p.pausa) : !!p.pausa;
    if (enCurso) {
        if (delCatalogo && detalle.del_catalogo) {
            const hasta = diaEnTexto(detalle.hasta);
            return {
                accion: 'quitar', pausar: false, boton: 'Quitar la pausa', testid: 'quitar-pausa', marcado: true,
                aviso: `${hasta ? `En pausa hasta el ${hasta}` : 'En pausa'}. Al terminar vuelve a la semana 1 y su plan se alarga 4 semanas`,
                avisoTestid: 'en-pausa-hasta',
                confirmar: {
                    title: '¿Quitar la pausa?',
                    description: hasta ? `Su pausa dura hasta el ${hasta}: se la quitas antes de tiempo.`
                        : 'Su pausa todavía no ha terminado: se la quitas antes de tiempo.',
                    confirmLabel: 'Quitar la pausa',
                },
                errorFijo: 'No se pudo quitar la pausa', delCatalogo,
            };
        }
        const desde = diaEnTexto((p.pausa && p.pausa.desde) || detalle.desde);
        return {
            accion: 'quitar', pausar: false, boton: 'Quitar la pausa', testid: 'quitar-pausa', marcado: true,
            aviso: desde ? `En pausa desde el ${desde}: su semana no se mueve hasta que le quites la pausa.` : null,
            avisoTestid: 'en-pausa-desde',
            confirmar: QUITAR_DE_SIEMPRE,
            errorFijo: 'No se pudo quitar la pausa', delCatalogo,
        };
    }

    return {
        accion: 'pausar', pausar: true, boton: 'Pausar', testid: 'pausar-cliente', marcado: false,
        aviso: null, avisoTestid: null,
        confirmar: {
            title: '¿Pausar a este cliente?',
            description: delCatalogo
                ? 'La pausa queda pedida: empieza cuando acabe la semana 4 o la 8 de su plan y dura 4 semanas. Su plan se alarga esas 4 semanas y, al terminar, vuelve a la semana 1.'
                : 'Sigue pudiendo usar la app, pero su semana no se mueve hasta que le quites la pausa. Al quitarla, lo que tiene pagado se le alarga las semanas que haya estado pausado.',
            confirmLabel: 'Pausar',
        },
        errorFijo: 'No se pudo pausar', delCatalogo,
    };
};

/** Lo que hay que avisar ANTES de desactivar a este cliente (12-10): con una pausa pedida,
 *  desactivarlo la retira (el reloj lo apunta: "La pausa pedida se retira sola: se da de
 *  baja") y al volver a activarlo no vuelve. El diálogo no lo decía. Sin pausa pedida,
 *  cadena vacía. */
export const avisoDePausaAlDesactivar = (profile) => (
    (profile || {}).pausa_estado === 'pedida'
        ? ' Tiene una pausa pedida: al desactivarlo se retira, y si luego lo activas habrá que pedirla otra vez.'
        : '');

/** El aviso de «hecho» tras el POST, según lo que respondió el servidor. `decision` es lo
 *  que devolvió `estadoDeLaPausa` antes de mandar. */
export const mensajeTrasLaPausa = (decision, respuesta) => {
    const r = respuesta || {};
    const accion = (decision && decision.accion) || 'pausar';
    if (r.pausa_estado === 'pedida') return fraseDePedida(r.empieza_el);
    if (r.pausa_estado === 'en_curso') {
        const hasta = r.del_catalogo ? diaEnTexto(r.hasta) : null;
        return hasta ? `Cliente en pausa hasta el ${hasta}` : 'Cliente en pausa';
    }
    if (r.pausa_estado === 'ninguna' && accion === 'retirar') return 'Pausa pedida retirada';
    if (accion === 'pausar') return 'Cliente en pausa';
    if (r.semanas) return `Pausa quitada: ${r.semanas === 1 ? '1 semana corrida' : `${r.semanas} semanas corridas`}`;
    return accion === 'retirar' ? 'Pausa pedida retirada' : 'Pausa quitada';
};

/** La frase que se enseña si el POST falla. El 422 de esta ruta trae una frase ya humana
 *  («La pausa solo puede empezar al acabar la semana 4 o la 8 del plan.»): se usa si viene
 *  como texto; si no, la fija. Lo demás lo decide quien llama (los 409 de siempre). */
export const fraseDelRechazo = (error, fija) => {
    const res = error && error.response;
    const detalle = res && res.data && res.data.detail;
    if (res && res.status === 422) return (typeof detalle === 'string' && detalle.trim()) ? detalle.trim() : fija;
    return null;
};
